import { useEffect, useState } from "react";
import { regions } from "../../../const";
import { useMutation, useQuery } from "@apollo/client";
import { UPDATE_PROFILE } from "../../../GraphQL/settings/mutations";
import { GET_INDUSTRIES, GET_LOCALITIES } from "../../../GraphQL/settings/queries";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import { MapPin, Building, Globe } from "lucide-react";
import { EToastColorStatus, EToastPosition, IProfile } from "../../../interfaces";
import Notification from "../../Notification";

const list_region = regions.map(item => ({value: item, label: item}));

const IndustrySettings = ({ profile }: IProfile ) => {
  const { error, loading, data } = useQuery(GET_INDUSTRIES, {
    onCompleted: (data) => setIndustryTypes(data?.industries),
  });

  const {
    error: errorLocalities,
    loading: loadingLocalities,
    data: dataLocalities,
  } = useQuery(GET_LOCALITIES);

  useEffect(() => {
    setLocalities(dataLocalities?.localities?.items)
  }, [dataLocalities]);

  const [showToast, setShowToast] = useState(false);
  const [regionValues, setRegionValues] = useState<string[]>(profile?.industryPreferences?.regions || []);
  const [industryValues, setIndustryValues] = useState<string[]>(profile?.industryPreferences?.industries || []);
  const [industryTypes, setIndustryTypes] = useState<string[]>([]);
  const listOfIndustries = industryTypes.map(item => ({value: item, label: item}));
  const [localities, setLocalities] = useState<any[]>([]);

  const listOfLocalities = localities.map(locality => ({
    value: String(locality.id),
    label: locality.name,
  }));

  const [localityValues, setLocalityValues] = useState<string[]>([]);

  useEffect(() => {
    const initLocalityIds = profile?.industryPreferences?.localityIds || [];
    setLocalityValues(initLocalityIds.map(id => String(id)));
  }, [profile, localities]);

  const [updateProfile] = useMutation(UPDATE_PROFILE, {
    onCompleted: () => {
      setShowToast(true);
    },
  });

  const handleSaveIndustrySettings = () => {
    updateProfile({
      variables: {
        firstName: profile?.firstName,
        lastName: profile?.lastName,
        phoneNumber: profile?.phoneNumber,
        industryPreferences: {
          regions: regionValues,
          industries: industryValues,
          localityIds: localityValues.map(id => parseInt(id)),
        },
      },
    });
  }

  return (
    <>
      <Notification
        delay={3000}
        toastData={{show: showToast, color: EToastColorStatus.SUCCESS, position: EToastPosition.BOTTOM_END, message: "Success Update Industry Settings"}}
        onClose={() => setShowToast(false)}
      />

      <div className="space-y-xl">
        {/* Regions */}
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-xl">
            <div className="flex items-start gap-md mb-md">
              <MapPin className="w-5 h-5 text-teal-600 dark:text-teal-400 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <Label htmlFor="regions" className="font-serif text-lg text-slate-900 dark:text-slate-100">
                  Regions
                </Label>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-sm">
                  Select the regions you work in
                </p>
                <MultiSelect
                  options={list_region}
                  selected={regionValues}
                  onChange={setRegionValues}
                  placeholder="Select regions..."
                  className="w-full"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Industries */}
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-xl">
            <div className="flex items-start gap-md mb-md">
              <Building className="w-5 h-5 text-teal-600 dark:text-teal-400 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <Label htmlFor="industries" className="font-serif text-lg text-slate-900 dark:text-slate-100">
                  Industries
                </Label>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-sm">
                  Select the industries you specialize in
                </p>
                {error && (
                  <p className="text-sm text-destructive mb-sm">Error loading industries: {error.message}</p>
                )}
                {loading ? (
                  <p className="text-sm text-slate-600 dark:text-slate-400">Loading industries...</p>
                ) : (
                  <MultiSelect
                    options={listOfIndustries}
                    selected={industryValues}
                    onChange={setIndustryValues}
                    placeholder="Select industries..."
                    className="w-full"
                  />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Localities */}
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-xl">
            <div className="flex items-start gap-md mb-md">
              <Globe className="w-5 h-5 text-teal-600 dark:text-teal-400 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <Label htmlFor="localities" className="font-serif text-lg text-slate-900 dark:text-slate-100">
                  Localities
                </Label>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-sm">
                  Select specific localities you cover
                </p>
                {errorLocalities && (
                  <p className="text-sm text-destructive mb-sm">Error loading localities: {errorLocalities.message}</p>
                )}
                {loadingLocalities ? (
                  <p className="text-sm text-slate-600 dark:text-slate-400">Loading localities...</p>
                ) : (
                  <MultiSelect
                    options={listOfLocalities}
                    selected={localityValues}
                    onChange={setLocalityValues}
                    placeholder="Select localities..."
                    className="w-full"
                  />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end pt-lg border-t border-slate-200 dark:border-slate-700">
          <Button
            size="sm"
            variant="default"
            onClick={handleSaveIndustrySettings}
            className="min-w-[120px]"
          >
            Save Changes
          </Button>
        </div>
      </div>
    </>
  )
}

export default IndustrySettings;
